import { filter, keyBy, map, omit, orderBy, uniq } from "lodash";
import { STORAGE_CURRENT_ROOM } from "../../constants/localstorage";
import { getStorage } from "../../libs/storage";
import { GroupRoom, Room } from "../../proto/Global_pb";
import { RoomMembersType, RoomStateType, RoomsType } from "./types";
import { createReducer } from "@reduxjs/toolkit";
import {
  addAdmin,
  addRoomMembers,
  addRooms,
  clearAllRooms,
  deleteRoomAction,
  editRoom,
  editRoomDescription,
  editRoomExtra,
  editRoomUsername,
  filterRooms,
  kickAdmin,
  removeRoom,
  removeRoomMember,
  removeRoomMembers,
  setCurrentRoom,
  setCurrentRoomExtra,
  setRoomAction,
  updateChannelAvatar,
  updateClientMuteRoom,
  updateFetchRoomCompleted,
  updateGroupAvatar,
  updateLastMessage,
  updateLastMessageStatus,
  updateRoomMemberList,
  updateRoomMemberRights,
  updateRoomReadOnly,
  updateRoomStatus,
} from "./actions";
import { RoomFilter } from "../../constants/globals";

const initialState: RoomStateType = {
  rooms: {},
  roomList: [],
  roomListStatus: "pending",
  isCompletedRoomList: false,
  roomMembers: {},
  roomAction: {},
  currentRoom: getStorage(STORAGE_CURRENT_ROOM) || undefined,
  prevRoom: undefined,
  currentRoomExtra: { notJoined: false, subscribed: false },
  filterType: -1,
};

const sortRoomList = (rooms: RoomsType, filterId: RoomFilter) => {
  const filterHash: any = {
    [RoomFilter.All]: (room: Room.AsObject) => room,
    [RoomFilter.ExpertGroup]: (room: Room.AsObject) => room.type === Room.Type.GROUP && room.appId > 8 && room.appId < 12,
    [RoomFilter.ExpertChannel]: (room: Room.AsObject) => room.type === Room.Type.CHANNEL && room.appId > 8 && room.appId < 12,
    [RoomFilter.Chat]: (room: Room.AsObject) => room.type === Room.Type.CHAT,
  };
  let filterByType = filter(
    orderBy(
      rooms,
      [
        "pinId",
        (o) => {
          return (!!o?.lastMessage?.updateTime || !!o?.lastMessage?.createTime) && (o?.lastMessage?.updateTime || o?.lastMessage?.createTime);
        },
        "draft.message",
      ],
      ["desc", "desc", "desc"]
    ),
    filterHash[filterId]
  );
  return filter(filterByType, (room) => room.chatRoomExtra?.peer?.id !== "2297310");
};

// (room) => !!room.lastMessage
export const roomsReducer = createReducer(initialState, (builder) => {
  builder
    .addCase(addRooms, (state, action) => {
      const rooms = {
        ...state.rooms,
        ...keyBy(action.payload, "id"),
      };
      state.rooms = rooms;
      state.roomList = map(
        filter(sortRoomList(rooms, state.filterType), (room) => room.isParticipant),
        "id"
      );
    })
    .addCase(clearAllRooms, (state, action) => {
      state = initialState;
    })
    .addCase(editRoom, (state, action) => {
      if (state.rooms[action.payload.roomId] === undefined) return state;
      const rooms = {
        ...state.rooms,
        [action.payload.roomId]: {
          ...state.rooms[action.payload.roomId],
          ...action.payload.room,
        },
      };
      state.rooms = rooms;
      state.roomList = map(
        filter(sortRoomList(rooms, state.filterType), (room) => room.isParticipant),
        "id"
      );
    })
    .addCase(filterRooms, (state, action) => {
      state.filterType = action.payload;
      state.roomList = map(
        filter(sortRoomList(state.rooms, state.filterType), (room) => room.isParticipant),
        "id"
      );
    })
    .addCase(editRoomUsername, (state, action) => {
      if (state.rooms[action.payload.roomId] === undefined) return state;
      let groupRoomExtra = state.rooms[action.payload.roomId]?.["groupRoomExtra"];
      if (groupRoomExtra?.publicExtra !== undefined) {
        groupRoomExtra.publicExtra.username = action.payload.username;
      }
      let channelRoomExtra = state.rooms[action.payload.roomId]?.["channelRoomExtra"];
      if (channelRoomExtra?.publicExtra !== undefined) {
        channelRoomExtra.publicExtra.username = action.payload.username;
      }
      state.rooms[action.payload.roomId].groupRoomExtra = groupRoomExtra;
      state.rooms[action.payload.roomId].channelRoomExtra = channelRoomExtra;
    })
    .addCase(editRoomDescription, (state, action) => {
      if (state.rooms[action.payload.roomId] === undefined) return state;
      let groupRoomExtra = state.rooms[action.payload.roomId]?.["groupRoomExtra"];
      if (groupRoomExtra !== undefined) {
        groupRoomExtra.description = action.payload.description;
      }
      let channelRoomExtra = state.rooms[action.payload.roomId]?.["channelRoomExtra"];
      if (channelRoomExtra !== undefined) {
        channelRoomExtra.description = action.payload.description;
      }
      state.rooms[action.payload.roomId].groupRoomExtra = groupRoomExtra;
      state.rooms[action.payload.roomId].channelRoomExtra = channelRoomExtra;
    })
    .addCase(editRoomExtra, (state, action) => {
      if (state.rooms[action.payload.roomId] === undefined) return state;
      let groupRoomExtra = state.rooms[action.payload.roomId]?.["groupRoomExtra"];
      if (groupRoomExtra !== undefined && action.payload.groupRoomExtra) {
        groupRoomExtra = {
          ...groupRoomExtra,
          ...action.payload.groupRoomExtra,
        };
      }
      let channelRoomExtra = state.rooms[action.payload.roomId]?.["channelRoomExtra"];
      if (channelRoomExtra !== undefined && action.payload.channelRoomExtra) {
        channelRoomExtra = {
          ...channelRoomExtra,
          ...action.payload.channelRoomExtra,
        };
      }
      state.rooms[action.payload.roomId].groupRoomExtra = groupRoomExtra;
      state.rooms[action.payload.roomId].channelRoomExtra = channelRoomExtra;
    })
    .addCase(removeRoom, (state, action) => {
      state.rooms = omit(state.rooms, action.payload);
      state.roomList = state.roomList.filter((roomId) => roomId !== action.payload);
    })
    .addCase(addRoomMembers, (state, action) => {
      const prevRoomState = state.rooms[action.payload.roomId];
      state.rooms[action.payload.roomId].channelRoomExtra = prevRoomState.channelRoomExtra && {
        ...prevRoomState.channelRoomExtra,
        participantsCount: prevRoomState.channelRoomExtra.participantsCount + 1,
      };
      state.rooms[action.payload.roomId].groupRoomExtra = prevRoomState.groupRoomExtra && { ...prevRoomState.groupRoomExtra, participantsCount: prevRoomState.groupRoomExtra.participantsCount + 1 };

      state.roomMembers[action.payload.roomId] = {
        ...state.roomMembers[action.payload.roomId],
        ...(keyBy(action.payload.memberList, "userid") as RoomMembersType[number]),
      };
    })
    .addCase(updateRoomMemberList, (state, action) => {
      state.rooms[action.payload.roomId] = { ...state.rooms[action.payload.roomId] };
      state.roomMembers[action.payload.roomId] = {
        ...state.roomMembers[action.payload.roomId],
        ...(keyBy(action.payload.memberList, "userid") as RoomMembersType[number]),
      };
    })
    .addCase(updateRoomMemberRights, (state, action) => {
      state.rooms[action.payload.roomId].groupRoomExtra = {
        ...state.rooms[action.payload.roomId]?.groupRoomExtra!,
        roomRights: { ...action.payload.roomRights },
      };
    })
    .addCase(removeRoomMember, (state, action) => {
      const prevRoomState = state.rooms[action.payload.roomId];
      const extra = prevRoomState?.groupRoomExtra
        ? { groupRoomExtra: { ...prevRoomState.groupRoomExtra, participantsCount: prevRoomState.groupRoomExtra.participantsCount - 1 } }
        : prevRoomState?.channelRoomExtra && { channelRoomExtra: { ...prevRoomState.channelRoomExtra, participantsCount: prevRoomState.channelRoomExtra.participantsCount - 1 } };

      state.rooms[action.payload.roomId] = {
        ...state.rooms[action.payload.roomId],
        ...extra,
      };
      state.roomMembers = {
        ...state.roomMembers,
        [action.payload.roomId]: omit(state.roomMembers[action.payload.roomId], action.payload.memberId),
      };
    })
    .addCase(removeRoomMembers, (state, action) => {
      state.roomMembers = omit(state.roomMembers, action.payload);
    })
    .addCase(addAdmin, (state, action) => {
      let rooms: RoomsType;
      if (action.payload.isMine) {
        rooms = {
          ...state.rooms,
          [action.payload.roomId]: {
            ...state.rooms[action.payload.roomId],
            groupRoomExtra: {
              ...(state.rooms[action.payload.roomId].groupRoomExtra as any),
              role: GroupRoom.Role.ADMIN,
            },
            permission: {
              ...(state.rooms[action.payload.roomId].permission as any),
              ...action.payload.permission,
            },
          },
        };
      } else {
        rooms = { ...state.rooms };
      }

      state.rooms = rooms;
      state.roomMembers[action.payload.roomId][action.payload.memberId] = omit(
        {
          ...state.roomMembers[action.payload.roomId]?.[action.payload.memberId],
          role: GroupRoom.Role.ADMIN,
          adminrights: action.payload.adminrights,
        },
        "memberrights"
      );
    })
    .addCase(kickAdmin, (state, action) => {
      let rooms: RoomsType;
      if (action.payload.isMine) {
        rooms = {
          ...state.rooms,
          [action.payload.roomId]: {
            ...state.rooms[action.payload.roomId],
            groupRoomExtra: {
              ...(state.rooms[action.payload.roomId].groupRoomExtra as any),
              role: GroupRoom.Role.MEMBER,
            },
          },
        };
      } else {
        rooms = { ...state.rooms };
      }
      state.rooms = rooms;
      if (action.payload.memberRights !== undefined) {
        state.roomMembers[action.payload.roomId][action.payload.memberId] = omit(
          {
            ...state.roomMembers[action.payload.roomId]?.[action.payload.memberId],
            role: GroupRoom.Role.MEMBER,
            memberRights: action.payload.memberRights,
          },
          "adminrights"
        );
      } else {
        state.roomMembers[action.payload.roomId][action.payload.memberId] = omit(
          {
            ...state.roomMembers[action.payload.roomId]?.[action.payload.memberId],
            role: GroupRoom.Role.MEMBER,
          },
          "adminrights",
          "memberRights"
        );
      }
    })
    .addCase(setRoomAction, (state, action) => {
      let prevUserId = state.roomAction[action.payload.roomId]?.userId || [];
      state.roomAction[action.payload.roomId] = {
        userId: uniq([...prevUserId, ...action.payload.userId]),
        action: action.payload.action,
      };
    })
    .addCase(deleteRoomAction, (state, action) => {
      let prevUserId = state.roomAction[action.payload.roomId]?.userId || [];
      state.roomAction[action.payload.roomId] = {
        userId: [...prevUserId.filter((userId) => userId !== action.payload.userId)],
        action: action.payload.action,
      };
    })
    .addCase(setCurrentRoom, (state, action) => {
      state.currentRoom = action.payload;
    })
    .addCase(updateClientMuteRoom, (state, action) => {
      state.rooms[action.payload.roomId].roomMute = action.payload.roomMute;
    })
    .addCase(setCurrentRoomExtra, (state, action) => {
      state.currentRoomExtra = {
        ...state.currentRoomExtra,
        ...action.payload,
      };
    })
    .addCase(updateRoomReadOnly, (state, action) => {
      state.rooms[action.payload].readOnly = false;
    })
    .addCase(updateLastMessageStatus, (state, action) => {
      state.rooms[action.payload.roomId].lastMessage = { ...state.rooms[action.payload.roomId]?.lastMessage!, status: action.payload.status };
    })
    .addCase(updateLastMessage, (state, action) => {
      state.rooms[action.payload.roomId].lastMessage = { ...state.rooms[action.payload.roomId]?.lastMessage!, message: action.payload.lastMessage };
    })
    .addCase(updateChannelAvatar, (state, action) => {
      state.rooms[action.payload.roomId].channelRoomExtra = {
        ...state.rooms[action.payload.roomId].channelRoomExtra!,
        avatar: action.payload.avatar,
      };
    })
    .addCase(updateGroupAvatar, (state, action) => {
      state.rooms[action.payload.roomId].groupRoomExtra = {
        ...state.rooms[action.payload.roomId].groupRoomExtra!,
        avatar: action.payload.avatar,
      };
    })
    .addCase(updateRoomStatus, (state, action) => {
      state.roomListStatus = action.payload;
    })
    .addCase(updateFetchRoomCompleted, (state, action) => {
      state.isCompletedRoomList = action.payload;
    });
});
